// invoice_form_v1: InvoiceForm modal — create + edit
import React, { useState, useEffect, useMemo } from 'react';
import API from '../api';
import { X, Plus, Trash2, Save, Package, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const PAYMENT_METHODS = [
  { value: 'promptpay',    label: 'PromptPay',  icon: '💚', color: '#22c55e' },
  { value: 'bank_transfer',label: 'โอนธนาคาร', icon: '🏦', color: '#3b82f6' },
  { value: 'usdt_trc20',   label: 'USDT TRC20', icon: '🔴', color: '#ef4444' },
  { value: 'usdt_erc20',   label: 'USDT ERC20', icon: '🔵', color: '#8b5cf6' },
];

const emptyItem = () => ({ description: '', quantity: 1, unit_price: 0, sort_order: 0 });

const emptyForm = () => ({
  /* invoice_customer_v1 */
  tenant_customer_id: '',
  recipient_name: '',
  recipient_phone: '',
  recipient_address: '',
  currency: 'THB',
  issue_date: new Date().toISOString().slice(0, 10),
  due_date: '',
  note: '',
  payment_method: 'promptpay',
  crypto_wallet_address: '',
  crypto_network: '',
  usdt_rate: '',
  items: [emptyItem()],
});

// Convert backend invoice + items → form state (for edit mode)
function invoiceToForm(invoice, items) {
  return {
    /* invoice_customer_v1 */
    tenant_customer_id: invoice.tenant_customer_id || '',
    recipient_name: invoice.recipient_name || '',
    recipient_phone: invoice.recipient_phone || '',
    recipient_address: invoice.recipient_address || '',
    currency: invoice.currency || 'THB',
    issue_date: invoice.issue_date ? String(invoice.issue_date).slice(0, 10) : '',
    due_date: invoice.due_date ? String(invoice.due_date).slice(0, 10) : '',
    note: invoice.note || '',
    payment_method: invoice.payment_method || 'promptpay',
    crypto_wallet_address: invoice.crypto_wallet_address || '',
    crypto_network: invoice.crypto_network || '',
    usdt_rate: invoice.usdt_rate != null ? String(invoice.usdt_rate) : '',
    items: items && items.length > 0
      ? items.map((it, i) => ({
          description: it.description || '',
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price) || 0,
          sort_order: typeof it.sort_order === 'number' ? it.sort_order : i,
        }))
      : [emptyItem()],
  };
}

export default function InvoiceForm({ open, editId, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [products, setProducts] = useState([]);
  /* invoice_customer_v1: customers list for picker */
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Load products for picker (once when modal opens)
  useEffect(() => {
    if (!open) return;
    API.get('/api/products?active=true&limit=500')
      .then(r => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProducts([]));
  }, [open]);

  /* invoice_customer_v1: Load customers when modal opens */
  useEffect(() => {
    if (!open) return;
    API.get('/api/customers?active=true&limit=500')
      .then(r => setCustomers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCustomers([]));
  }, [open]);

  // Load invoice data when editId changes
  useEffect(() => {
    if (!open) return;
    if (!editId) {
      setForm(emptyForm());
      return;
    }
    setLoading(true);
    API.get('/api/invoices/' + editId)
      .then(r => {
        const { invoice, items } = r.data;
        setForm(invoiceToForm(invoice, items));
      })
      .catch(e => {
        toast.error('โหลดบิลไม่สำเร็จ');
        onClose && onClose();
      })
      .finally(() => setLoading(false));
  }, [editId, open, onClose]);

  // Computed totals
  const subtotal = useMemo(() => {
    return form.items.reduce((sum, it) => {
      const q = Number(it.quantity) || 0;
      const p = Number(it.unit_price) || 0;
      return sum + q * p;
    }, 0);
  }, [form.items]);

  const usdtPreview = useMemo(() => {
    const rate = Number(form.usdt_rate);
    if (form.currency !== 'THB' || !isFinite(rate) || rate <= 0) return null;
    return subtotal / rate;
  }, [subtotal, form.usdt_rate, form.currency]);

  // ---------- handlers ----------
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setItem = (idx, k, v) => {
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === idx ? { ...it, [k]: v } : it),
    }));
  };
  const addItem = () => setForm(f => ({
    ...f,
    items: [...f.items, { ...emptyItem(), sort_order: f.items.length }],
  }));
  const removeItem = (idx) => {
    setForm(f => ({
      ...f,
      items: f.items.length === 1 ? f.items : f.items.filter((_, i) => i !== idx),
    }));
  };
  /* invoice_customer_v1: pick customer → auto-fill recipient fields */
  const pickCustomer = (customerId) => {
    if (!customerId) {
      setForm(f => ({ ...f, tenant_customer_id: '' }));
      return;
    }
    const c = customers.find(x => x.id === customerId);
    if (!c) return;
    setForm(f => ({
      ...f,
      tenant_customer_id: c.id,
      recipient_name: c.name || f.recipient_name,
      recipient_phone: c.phone || f.recipient_phone,
      recipient_address: c.address || f.recipient_address,
    }));
  };

  const pickProduct = (idx, productId) => {
    if (!productId) return;
    const p = products.find(x => x.id === productId);
    if (!p) return;
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === idx
        ? { ...it, description: p.name, unit_price: Number(p.unit_price) || 0, quantity: it.quantity || 1 }
        : it),
    }));
  };

  const validate = () => {
    /* invoice_customer_v1: customer required when creating new */
    if (!editId && !form.tenant_customer_id) {
      return 'กรุณาเลือกลูกค้าก่อนสร้างบิล';
    }
    if (!form.recipient_name.trim()) return 'กรุณากรอกชื่อลูกค้า';
    const validItems = form.items.filter(it => it.description.trim() && Number(it.quantity) > 0 && Number(it.unit_price) >= 0);
    if (validItems.length === 0) return 'กรุณาเพิ่มรายการอย่างน้อย 1 รายการ';
    if (subtotal <= 0) return 'ยอดรวมต้องมากกว่า 0';
    if (form.currency === 'THB' && form.usdt_rate.trim() !== '') {
      const rate = Number(form.usdt_rate);
      if (!isFinite(rate) || rate <= 0) return 'อัตรา USDT ต้องเป็นตัวเลข > 0';
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }

    setSaving(true);
    try {
      const payload = {
        /* invoice_customer_v1 */
        tenant_customer_id: form.tenant_customer_id || null,
        recipient_name: form.recipient_name.trim(),
        recipient_phone: form.recipient_phone.trim() || null,
        recipient_address: form.recipient_address.trim() || null,
        currency: form.currency,
        issue_date: form.issue_date || null,
        due_date: form.due_date || null,
        note: form.note.trim() || null,
        payment_method: form.payment_method || null,
        crypto_wallet_address: (form.payment_method === 'usdt_trc20' || form.payment_method === 'usdt_erc20')
          ? form.crypto_wallet_address.trim() || null : null,
        crypto_network: form.payment_method === 'usdt_trc20' ? 'TRC20'
          : (form.payment_method === 'usdt_erc20' ? 'ERC20' : null),
        usdt_rate: form.usdt_rate.trim() ? Number(form.usdt_rate) : null,
        items: form.items
          .filter(it => it.description.trim())
          .map((it, i) => ({
            description: it.description.trim(),
            quantity: Number(it.quantity) || 0,
            unit_price: Number(it.unit_price) || 0,
            sort_order: i,
          })),
      };

      if (editId) {
        await API.put('/api/invoices/' + editId, payload);
        toast.success('บันทึกบิลสำเร็จ');
      } else {
        await API.post('/api/invoices', payload);
        toast.success('สร้างบิลสำเร็จ');
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'บันทึกบิลไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div onClick={onClose}
      style={{
        position:'fixed', inset:0, background:'rgba(15,23,42,0.6)',
        display:'flex', alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent:'center', zIndex: 1000,
        padding: isMobile ? 0 : 16, overflowY:'auto',
      }}>
      <div onClick={e => e.stopPropagation()}
        style={{
          background:'#fff', borderRadius: isMobile ? 0 : 14,
          width:'100%', maxWidth: 880,
          maxHeight: isMobile ? '100vh' : '92vh',
          display:'flex', flexDirection:'column',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}>
        {/* Sticky header */}
        <div style={{
          padding: isMobile ? '14px 16px' : '18px 24px',
          borderBottom:'1px solid #e2e8f0',
          display:'flex', alignItems:'center', justifyContent:'space-between',
          position:'sticky', top: 0, background:'#fff', zIndex: 2,
          borderRadius: isMobile ? 0 : '14px 14px 0 0',
        }}>
          <h2 style={{ margin:0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color:'#0f172a' }}>
            {editId ? '✏️ แก้ไขบิล' : '➕ สร้างบิลใหม่'}
          </h2>
          <button onClick={onClose}
            style={{ background:'none', border:'none', cursor:'pointer', padding: 6 }}>
            <X size={22} color="#94a3b8" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: isMobile ? 14 : 22, overflowY:'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign:'center', color:'#94a3b8' }}>
              กำลังโหลดข้อมูลบิล...
            </div>
          ) : (
            <>
              {/* Recipient section */}
              <SectionTitle>
                📇 ข้อมูลผู้รับ
                {customers.length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 400, color:'#94a3b8', marginLeft: 8 }}>
                    (เลือกจากรายชื่อลูกค้า — {customers.length} คน)
                  </span>
                )}
              </SectionTitle>

              {/* invoice_customer_v1: customer picker */}
              <div style={{
                background:'#e0f2fe', borderRadius: 10, padding: 12, marginBottom: 12,
                border: '1px solid #bae6fd',
              }}>
                <label style={{ display:"block", fontSize:12, fontWeight:600, color:"#0284c7", marginBottom:4 }}>
                  เลือกลูกค้า {!editId && <span style={{ color: '#dc2626' }}>*</span>}
                </label>
                <select value={form.tenant_customer_id || ''}
                  onChange={e => pickCustomer(e.target.value)}
                  style={{
                    ...inputStyle(isMobile),
                    background:'#fff', border:'1px solid #bae6fd',
                    cursor:'pointer',
                  }}>
                  <option value="">— ไม่ระบุลูกค้า — </option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.telegram_chat_id ? ' — ✈️ ' + c.telegram_chat_id : ''}
                    </option>
                  ))}
                </select>
                {customers.length === 0 && (
                  <div style={{ fontSize: 10, color:'#94a3b8', marginTop: 6 }}>
                    ยังไม่มีลูกค้า — สร้างได้ที่หน้า "ลูกค้า" (sidebar)
                  </div>
                )}
                {form.tenant_customer_id && (
                  <div style={{ fontSize: 10, color:'#0c4a7c', marginTop: 6 }}>
                    💡 ข้อมูลด้านล่าง auto-fill จากลูกค้า — แก้ไขได้
                  </div>
                )}
              </div>

              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <Field label="ชื่อลูกค้า *" isMobile={isMobile}>
                  <input value={form.recipient_name} onChange={e => setField('recipient_name', e.target.value)}
                    placeholder="เช่น คุณสมชาย"
                    style={inputStyle(isMobile)} />
                </Field>
                <Field label="เบอร์โทร" isMobile={isMobile}>
                  <input value={form.recipient_phone} onChange={e => setField('recipient_phone', e.target.value)}
                    placeholder="081-234-5678"
                    style={inputStyle(isMobile)} />
                </Field>
              </div>
              <Field label="ที่อยู่" isMobile={isMobile}>
                <input value={form.recipient_address} onChange={e => setField('recipient_address', e.target.value)}
                  placeholder="ที่อยู่จัดส่งใบกำกับ (ถ้ามี)"
                  style={inputStyle(isMobile)} />
              </Field>

              {/* Dates + currency */}
              <SectionTitle style={{ marginTop: 18 }}>📅 วันที่ + สกุลเงิน</SectionTitle>
              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <Field label="วันที่ออกบิล" isMobile={isMobile}>
                  <input type="date" value={form.issue_date} onChange={e => setField('issue_date', e.target.value)}
                    style={inputStyle(isMobile)} />
                </Field>
                <Field label="กำหนดชำระ" isMobile={isMobile}>
                  <input type="date" value={form.due_date} onChange={e => setField('due_date', e.target.value)}
                    style={inputStyle(isMobile)} />
                </Field>
                <Field label="สกุลเงิน" isMobile={isMobile}>
                  <select value={form.currency} onChange={e => setField('currency', e.target.value)}
                    style={inputStyle(isMobile)}>
                    <option value="THB">฿ THB</option>
                    <option value="USDT">USDT</option>
                  </select>
                </Field>
                <Field label="อัตรา USDT (THB/USDT)" isMobile={isMobile}>
                  <input type="number" step="0.01" min="0"
                    value={form.usdt_rate} onChange={e => setField('usdt_rate', e.target.value)}
                    placeholder={form.currency === 'THB' ? 'เช่น 36.50' : '(สำหรับ THB เท่านั้น)'}
                    disabled={form.currency !== 'THB'}
                    style={{ ...inputStyle(isMobile), opacity: form.currency === 'THB' ? 1 : 0.5 }} />
                </Field>
              </div>

              {/* Items */}
              <SectionTitle style={{ marginTop: 18 }}>
                📦 รายการ
                {products.length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 400, color:'#94a3b8', marginLeft: 8 }}>
                    (เลือกจากสินค้าได้ — {products.length} รายการ)
                  </span>
                )}
              </SectionTitle>
              <div style={{
                background:'#f8fafc', borderRadius: 10, padding: isMobile ? 10 : 14, marginBottom: 12,
              }}>
                {form.items.map((it, idx) => (
                  <ItemRow key={idx} item={it} idx={idx} products={products}
                    isMobile={isMobile} canRemove={form.items.length > 1}
                    onChange={setItem} onPickProduct={pickProduct} onRemove={removeItem} />
                ))}
                <button onClick={addItem}
                  style={{
                    marginTop: 8, padding: '10px 16px', background:'#e0f2fe', color:'#0284c7',
                    border:'1px dashed #7dd3fc', borderRadius: 8, cursor:'pointer',
                    fontSize: 13, fontWeight: 500,
                    display:'inline-flex', alignItems:'center', gap: 6,
                  }}>
                  <Plus size={14} />เพิ่มรายการ
                </button>
              </div>

              {/* Total preview */}
              <div style={{
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                color:'#fff', borderRadius: 10, padding: isMobile ? 14 : 18,
                marginBottom: 18,
                display:'flex', justifyContent:'space-between', alignItems:'center',
                flexWrap:'wrap', gap: 10,
              }}>
                <span style={{ fontSize: 14, fontWeight: 500, opacity: 0.95 }}>ยอดรวม:</span>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800 }}>
                    {subtotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {' '}{form.currency === 'USDT' ? 'USDT' : '฿'}
                  </div>
                  {usdtPreview != null && (
                    <div style={{ fontSize: 12, opacity: 0.85 }}>
                      ≈ {usdtPreview.toFixed(2)} USDT
                    </div>
                  )}
                </div>
              </div>

              {/* Payment methods */}
              <SectionTitle>💳 วิธีรับเงิน</SectionTitle>
              <div style={{
                display:'grid',
                gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
                gap: 10, marginBottom: 14,
              }}>
                {PAYMENT_METHODS.map(pm => (
                  <PaymentCard key={pm.value} pm={pm}
                    active={form.payment_method === pm.value}
                    onClick={() => setField('payment_method', pm.value)} />
                ))}
              </div>

              {/* Conditional wallet input for USDT */}
              {(form.payment_method === 'usdt_trc20' || form.payment_method === 'usdt_erc20') && (
                <Field label={'Wallet ' + (form.payment_method === 'usdt_trc20' ? 'TRC20' : 'ERC20') + ' Address'} isMobile={isMobile}>
                  <input value={form.crypto_wallet_address}
                    onChange={e => setField('crypto_wallet_address', e.target.value)}
                    placeholder={form.payment_method === 'usdt_trc20' ? 'T...' : '0x...'}
                    style={inputStyle(isMobile)} />
                </Field>
              )}

              {/* Note */}
              <SectionTitle style={{ marginTop: 18 }}>📝 หมายเหตุ</SectionTitle>
              <textarea value={form.note} onChange={e => setField('note', e.target.value)}
                placeholder={form.payment_method === 'bank_transfer'
                  ? 'เช่น ธนาคารกสิกร 123-4-56789-0 บัญชี xxx'
                  : 'หมายเหตุเพิ่มเติม...'}
                rows={3}
                style={{ ...inputStyle(isMobile), resize:'vertical', fontFamily:'inherit' }} />
            </>
          )}
        </div>

        {/* Sticky footer */}
        <div style={{
          padding: isMobile ? '12px 16px' : '14px 24px',
          borderTop: '1px solid #e2e8f0', background:'#f8fafc',
          display:'flex', justifyContent: 'flex-end', gap: 10,
          position:'sticky', bottom: 0, zIndex: 2,
          borderRadius: isMobile ? 0 : '0 0 14px 14px',
        }}>
          <button onClick={onClose} disabled={saving}
            style={{
              padding: isMobile ? '12px 18px' : '10px 18px',
              background:'#f1f5f9', color:'#475569', border:'none', borderRadius: 8,
              fontSize: 13, fontWeight: 600, cursor:'pointer',
            }}>
            ยกเลิก
          </button>
          <button onClick={handleSave} disabled={saving || loading}
            style={{
              padding: isMobile ? '12px 22px' : '10px 22px',
              background: saving ? '#94a3b8' : '#0ea5e9',
              color:'#fff', border:'none', borderRadius: 8,
              fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
              display:'inline-flex', alignItems:'center', gap: 6,
            }}>
            <Save size={15} />
            {saving ? 'กำลังบันทึก...' : (editId ? 'บันทึก' : 'สร้างบิล')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- subcomponents ----------
function ItemRow({ item, idx, products, isMobile, canRemove, onChange, onPickProduct, onRemove }) {
  const lineTotal = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
  return (
    <div style={{
      background:'#fff', borderRadius: 8, padding: isMobile ? 10 : 12,
      marginBottom: 8, border: '1px solid #e2e8f0',
    }}>
      <div style={{
        display:'grid',
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr',
        gap: 8, alignItems:'flex-end',
      }}>
        <div>
          {products.length > 0 && (
            <select value=""
              onChange={e => { onPickProduct(idx, e.target.value); e.target.value = ''; }}
              style={{
                width:'100%', padding: '6px 8px', fontSize: 11,
                background:'#f0f9ff', border:'1px dashed #7dd3fc',
                borderRadius: 6, marginBottom: 4, cursor:'pointer', color:'#0284c7',
              }}>
              <option value="">🔽 เลือกจากสินค้า ({products.length})</option>
              {products.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} — {Number(p.unit_price).toLocaleString('th-TH')} ฿
                </option>
              ))}
            </select>
          )}
          <input value={item.description}
            onChange={e => onChange(idx, 'description', e.target.value)}
            placeholder="รายละเอียดสินค้า/บริการ"
            style={inputStyle(isMobile)} />
        </div>
        <div style={{ display:'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <div>
            <div style={{ fontSize: 10, color:'#64748b', marginBottom: 2 }}>จำนวน</div>
            <input type="number" min="0" step="0.01" value={item.quantity}
              onChange={e => onChange(idx, 'quantity', e.target.value)}
              style={{ ...inputStyle(isMobile), textAlign:'right' }} />
          </div>
          <div>
            <div style={{ fontSize: 10, color:'#64748b', marginBottom: 2 }}>ราคา/หน่วย</div>
            <input type="number" min="0" step="0.01" value={item.unit_price}
              onChange={e => onChange(idx, 'unit_price', e.target.value)}
              style={{ ...inputStyle(isMobile), textAlign:'right' }} />
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'flex-end', gap: 6 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color:'#64748b', marginBottom: 2 }}>รวม</div>
            <div style={{
              padding: isMobile ? '12px 14px' : '9px 12px',
              background:'#f0f9ff', borderRadius: 8,
              fontSize: 13, fontWeight: 700, color:'#0284c7',
              textAlign:'right', border:'1px solid #bae6fd',
            }}>
              {lineTotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          {canRemove && (
            <button onClick={() => onRemove(idx)} title="ลบรายการ"
              style={{
                padding: 8, background:'#fff1f2', color:'#ef4444',
                border:'none', borderRadius: 8, cursor:'pointer',
                display:'inline-flex', alignItems:'center', justifyContent:'center',
              }}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PaymentCard({ pm, active, onClick }) {
  return (
    <button onClick={onClick}
      style={{
        padding: '14px 12px', borderRadius: 10,
        background: active ? pm.color : '#fff',
        color: active ? '#fff' : '#334155',
        border: active ? '2px solid ' + pm.color : '2px solid #e2e8f0',
        cursor:'pointer', textAlign:'center', fontWeight: 600,
        fontSize: 13, transition: 'all 120ms',
        display:'flex', flexDirection:'column', alignItems:'center', gap: 4,
      }}>
      <div style={{ fontSize: 22 }}>{pm.icon}</div>
      <div>{pm.label}</div>
    </button>
  );
}

const SectionTitle = ({ children, style }) => (
  <div style={{
    fontSize: 13, fontWeight: 700, color:'#0f172a',
    marginBottom: 10, paddingBottom: 6, borderBottom:'2px solid #f1f5f9',
    ...(style || {}),
  }}>{children}</div>
);

function Field({ label, children, isMobile }) {
  return (
    <div>
      <label style={{
        display:'block', fontSize: 12, fontWeight: 600, color:'#64748b', marginBottom: 4,
      }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = (isMobile) => ({
  width:'100%', boxSizing:'border-box',
  padding: isMobile ? '12px 14px' : '9px 12px',
  borderRadius: 8, border: '1px solid #e2e8f0',
  fontSize: isMobile ? 16 : 14, outline:'none',
  background:'#fff',
});
